import React, { useEffect, useState } from "react";
import moment from "moment";
import { MAX_COURSES_PER_MONTH } from "../../../Utils/monthlyQuota";
import { isVerifiedTrainer } from "../../../Utils/VerifiedTrainerAmounts";
import TravelRepository, { ComplianceAlertInfo } from "../../../Repositories/TravelRepository";
import TravelStore from "../../DeclarationCourse/Travel/TravelStore";
import ComplianceAlert from "../../DeclarationCourse/Travel/ComplianceAlert";
import { useTravelScenario } from "../../DeclarationCourse/Travel/TravelModeSelector";
import { sessionEnd, sessionStart, sortSessionRows } from "../../DeclarationCourse/SessionList";
import { DV2 } from "../theme";
import { clock, dayLabel, euros, hours, initials, plural, studentFirstName, studentFullName } from "../format";
import { vocab } from "../vocabulary";
import { useDeclarationV2 } from "../DeclarationV2Context";
import { declarationHours, declarationPeriod, headlineSummary, planningSummary } from "../summary";
import WizardShell from "../layout/WizardShell";
import PlanningAside from "../layout/PlanningAside";
import { studentMonthFigures } from "../layout/StudentMonthPanel";
import { Avatar, Card, KeyValue, Notice, TextLink, useDv2Theme } from "../layout/Ui";
import { computeAmounts } from "../components/amounts";
import { isRelationTeachrChild, relationRemuneration } from "../components/relationTeachr";
import submitDeclaration, { DeclarationError } from "../api/submitDeclaration";


/** Article 199 sexdecies du CGI (Légifrance), ouvert par « En savoir plus ». */
const LEGAL_ARTICLE_URL = "https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000047850989";
/** Bloc du récapitulatif, replié par défaut avec son résumé sur une ligne, dépliable. */
const Section = ({ id, title, summary, onEdit, children }: { id: string; title: string; summary: React.ReactNode; onEdit: () => void; children: React.ReactNode }) => {
    const { primary, fontFamily } = useDv2Theme();
    const [open, setOpen] = useState(false);
    return (
        <div style={{ borderBottom: `1px solid ${DV2.borderSoft}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 22px" }}>
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    aria-expanded={open}
                    aria-controls={id}
                    style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 12, background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", fontFamily, color: DV2.text }}
                >
                    <span aria-hidden style={{ color: primary, fontSize: 18, lineHeight: 1, transform: open ? "rotate(90deg)" : "none", transition: "transform .15s", flexShrink: 0 }}>›</span>
                    <span style={{ minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 12, fontWeight: 800, color: DV2.placeholder, textTransform: "uppercase", letterSpacing: ".05em" }}>{title}</span>
                        {!open && <span style={{ display: "block", fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summary}</span>}
                    </span>
                </button>
                <TextLink label="Modifier" onClick={onEdit} />
            </div>
            {open && (
                <div id={id} style={{ padding: "0 22px 18px 52px", display: "flex", flexDirection: "column", gap: 10 }}>
                    {children}
                </div>
            )}
        </div>
    );
};

/**
 * Étape 4 (maquettes 1e et 2e) : une seule carte de synthèse par section
 * avec « Modifier », montants en trois tuiles, engagement légal en une ligne,
 * puis « Déclarer ». L'envoi reprend la chaîne de l'ancien parcours et
 * revérifie le scénario de déplacement avec les dates finales.
 */
const SummaryStep = () => {
    const { teachr, state, update, goTo } = useDeclarationV2();
    const { primary, fontFamily, brandName } = useDv2Theme();
    const student = state.student!;
    const child = student.child;
    const multiple = state.courseType === "multiple";
    const { checkTravelScenario, travelModals, travelLoading } = useTravelScenario(teachr);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [complianceAlert, setComplianceAlert] = useState<ComplianceAlertInfo | null>(TravelStore.getInfo(child?.clientId)?.complianceAlert ?? null);

    useEffect(() => {
        if (!teachr?.id || !child?.clientId) return undefined;
        let cancelled = false;
        TravelRepository.getTravelInfo(teachr.id, child.clientId)
            .then((info) => { if (!cancelled) setComplianceAlert(info.complianceAlert ?? null); })
            .catch(() => undefined);
        return () => { cancelled = true; };
    }, [teachr?.id, child?.clientId]);

    const hoursTotal = declarationHours(state);
    const amounts = computeAmounts(state.pricing.price, hoursTotal, teachr, child, state.pricing.deposit.enabled ? state.pricing.deposit.amount : 0);
    // relation Teach'r : tarif fixé par la plateforme, le prof ne voit que sa rémunération
    const relation = isRelationTeachrChild(child);
    const remuneration = relationRemuneration(child, hoursTotal);
    const hourlyShown = relation ? remuneration.hourly : amounts.price;
    const figures = studentMonthFigures(child, teachr);
    const period = declarationPeriod(state);
    const rows = sortSessionRows(state.sessions);
    const isVt = isVerifiedTrainer();
    const typeLabel = multiple ? vocab.multipleCoursesTitle() : vocab.singleCourseTitle();
    const subjectLabel = isVt ? null : state.pricing.subject?.fr_FR ?? null;
    const deposit = state.pricing.deposit;
    const sessionsCount = multiple ? state.sessions.length : 1;

    const declare = () => {
        if (!state.legalAccepted) {
            setError(`Vous devez certifier que ${multiple ? `ces ${vocab.courses()} ont` : `${vocab.thisCourse()} a`} lieu au domicile de ${vocab.yourClient()} pour continuer.`);
            return;
        }
        setError(null);
        checkTravelScenario(child, async () => {
            setSending(true);
            try {
                const { courseId } = await submitDeclaration(state, teachr);
                update({ declaredCourseId: courseId, declaredAt: new Date().toISOString(), step: "success", resumeStep: null });
                goTo("success");
            } catch (e: any) {
                setError(e instanceof DeclarationError ? e.message : "Une erreur est survenue, votre déclaration n'a pas été enregistrée. Réessayez dans quelques instants.");
            } finally {
                setSending(false);
            }
        }, { start: period.start, end: period.end });
    };

    const tile = (label: string, value: string, hint: string, color?: string) => (
        <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: DV2.muted, textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: color ?? DV2.text }}>{value}</div>
            <div style={{ fontSize: 12, color: DV2.muted, lineHeight: 1.4 }}>{hint}</div>
        </div>
    );

    return (
        <>
            {travelModals}
            <WizardShell
                title="Tout est bon ?"
                subtitle="Étape 4 sur 4, relisez avant de déclarer"
                onBack={() => goTo("pricing")}
                aside={<PlanningAside current={4} planningDetail={planningSummary(state)} pricingDetail={`${subjectLabel ? `${subjectLabel}, ` : ""}${euros(hourlyShown)} de l'heure`} hideMonthPanel />}
                footer={{
                    onPrevious: () => goTo("pricing"),
                    onNext: declare,
                    nextLabel: multiple ? `Déclarer les ${plural(sessionsCount, "séance")}` : `Déclarer ${vocab.theCourse()}`,
                    nextLoading: sending || travelLoading,
                    nextDisabled: sending || travelLoading,
                    extra: error ? <Notice tone="danger" id="dv2-summary-error">{error}</Notice> : undefined,
                }}
            >
                {complianceAlert && <ComplianceAlert alert={complianceAlert} onConfirmed={setComplianceAlert} />}

                <Card padding={0} style={{ overflow: "hidden", fontFamily }}>
                    <div style={{ padding: 22, display: "flex", alignItems: "center", gap: 14, borderBottom: `1px solid ${DV2.borderSoft}` }}>
                        <Avatar text={initials(child)} size={48} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 18, fontWeight: 800 }}>{studentFullName(child)}</div>
                            <div style={{ fontSize: 14, color: DV2.muted }}>{headlineSummary(state, typeLabel, subjectLabel)}</div>
                        </div>
                    </div>

                    <Section id="dv2-recap-client" title={`${vocab.client().charAt(0).toUpperCase() + vocab.client().slice(1)} et déroulement`} summary={`${studentFullName(child)}, avance immédiate active`} onEdit={() => goTo("student")}>
                        <KeyValue label={vocab.client().charAt(0).toUpperCase() + vocab.client().slice(1)} value={studentFullName(child)} />
                        <KeyValue label="Avance immédiate" value={`Active, ${figures.remainingCourses} ${vocab.courses()} restants sur ${MAX_COURSES_PER_MONTH} ce mois`} />
                        <div style={{ fontSize: 14, color: DV2.textSoft, lineHeight: 1.5 }}>
                            <span style={{ color: DV2.muted }}>Déroulement, </span>
                            {state.sessionDescriptionOverride ? state.sessionDescriptionOverride : "votre description habituelle, reprise sur le devis."}
                        </div>
                    </Section>

                    <Section id="dv2-recap-planning" title={multiple ? `Planning, plusieurs ${vocab.courses()}` : `Planning, ${vocab.singleCourseTitle().toLowerCase()}`} summary={planningSummary(state)} onEdit={() => goTo(multiple ? "sessions" : "planning")}>
                        {multiple ? (
                            <>
                                {rows.map((row) => {
                                    const start = sessionStart(row);
                                    const end = sessionEnd(row);
                                    return start && end ? <KeyValue key={row.key} label={dayLabel(start)} value={`${clock(start)} à ${clock(end)}, ${hours(row.duration)}`} /> : null;
                                })}
                                <KeyValue label="Durée totale" value={hours(hoursTotal)} />
                            </>
                        ) : (
                            period.start && period.end && <KeyValue label={dayLabel(period.start, true)} value={`${clock(period.start)} à ${clock(period.end)}, ${hours(state.single.duration)}`} />
                        )}
                        {state.note && (
                            <div style={{ fontSize: 14, color: DV2.textSoft, lineHeight: 1.5 }}>
                                <span style={{ color: DV2.muted }}>Note pour {studentFirstName(child)}, </span>{state.note}
                            </div>
                        )}
                    </Section>

                    <Section id="dv2-recap-pricing" title={isVt ? "Tarif" : `${vocab.subjectLabel()} et tarif`} summary={relation ? `${subjectLabel ? `${subjectLabel}, ` : ""}${euros(hourlyShown)} de l'heure` : `${subjectLabel ? `${subjectLabel}, ` : ""}${euros(amounts.price)} de l'heure payés par ${vocab.theClient()}${deposit.enabled && amounts.deposit > 0 ? `, acompte ${euros(amounts.deposit)}` : ""}`} onEdit={() => goTo("pricing")}>
                        {subjectLabel && <KeyValue label={vocab.subjectLabel()} value={subjectLabel} />}
                        <KeyValue label={relation ? "Rémunération horaire" : "Prix horaire payé"} value={euros(hourlyShown)} />
                        {deposit.enabled && amounts.deposit > 0 && (
                            <>
                                <KeyValue label="Acompte versé par le client" value={`${euros(amounts.deposit)} reçus le ${deposit.date ? moment(deposit.date).locale("fr").format("D MMMM YYYY") : "…"}`} />
                                <KeyValue label="Attestation sur l'honneur" value={deposit.attestationAccepted ? "Signée à la déclaration" : "À certifier"} />
                                <KeyValue label="Preuve du versement" value={deposit.proof?.filePath ?? "Jointe"} />
                            </>
                        )}
                    </Section>

                    {relation ? (
                        <div style={{ padding: "18px 22px" }} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {tile("Rémunération horaire", euros(remuneration.hourly), `tarif convenu pour ${studentFirstName(child)}`)}
                            {tile("Rémunération totale", euros(remuneration.total), `pour ${hours(hoursTotal)}, reversés par ${brandName}`, primary)}
                        </div>
                    ) : (
                    <div style={{ padding: "18px 22px" }} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {tile("Vous recevez", euros(amounts.received), amounts.coop ? `${euros(amounts.receivedHt)} hors TVA${amounts.vatSubject ? `, TVA ${euros(amounts.receivedVat)}` : ""}` : `${euros(amounts.price, 0)} saisis, ${euros(amounts.receivedHourly)} par heure`, primary)}
                        {tile(`Prix payé pour ${multiple ? `les ${vocab.courses()}` : vocab.theCourse()}`, euros(amounts.advance), `sur ${euros(amounts.totalTtc)} au total, avance immédiate non comprise`)}
                        {tile("Reste à sa charge", euros(amounts.remaining), `après avance immédiate ${euros(amounts.advance)}${amounts.deposit > 0 ? ` et acompte ${euros(amounts.deposit, 0)}` : ""}`)}
                    </div>
                    )}
                </Card>

                <Card style={{ padding: "16px 20px", fontFamily }}>
                    <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={state.legalAccepted}
                            onChange={(e) => { update({ legalAccepted: e.target.checked }); setError(null); }}
                            aria-describedby={error ? "dv2-summary-error" : undefined}
                            style={{ width: 22, height: 22, accentColor: primary, flexShrink: 0, marginTop: 1 }}
                        />
                        <span style={{ fontSize: 14, lineHeight: 1.5 }}>
                            Je certifie que {multiple ? `ces ${vocab.courses()} ont` : `${vocab.thisCourse()} a`} lieu au domicile de {vocab.myClient()}, conformément à l'article 199 sexdecies du Code général des impôts.{" "}
                            <TextLink label="En savoir plus" size={14} href={LEGAL_ARTICLE_URL} />
                        </span>
                    </label>
                </Card>

                <div style={{ fontSize: 13, color: DV2.muted, lineHeight: 1.5 }}>
                    Une fois la demande de paiement transmise à l'URSSAF, {vocab.yourClient()} sera informé par e-mail. Il disposera alors de 48 heures pour valider ou contester le paiement. Si aucune action n'est effectuée dans ce délai, le paiement sera automatiquement validé.
                </div>
            </WizardShell>
        </>
    );
};

export default SummaryStep;
